import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Save, RotateCcw, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { getDict, type Lang, LANGS, pathForLang } from "@/i18n/translations";
import {
  HOMEPAGE_SECTIONS,
  SECTION_LABELS,
  SECTION_SCHEMAS,
  LANG_LABELS,
  getPath,
  setPath,
  type HomepageSection,
  type FieldDef,
} from "@/lib/homepage-content";

export const Route = createFileRoute("/_authenticated/admin/homepage")({
  head: () => ({
    meta: [
      { title: "Admin — Homepage-teksten" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminHomepagePage,
});

type OverrideMap = Record<HomepageSection, Record<string, unknown>>;

const emptyOverrides = (): OverrideMap => ({
  hero: {},
  why: {},
  gallery: {},
  practical: {},
  travel: {},
  faq: {},
  footer: {},
});

function AdminHomepagePage() {
  const qc = useQueryClient();
  const [lang, setLang] = useState<Lang>("nl");
  const [overrides, setOverrides] = useState<OverrideMap>(emptyOverrides());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<HomepageSection | null>(null);

  const defaults = useMemo(() => getDict(lang), [lang]);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("homepage_content")
      .select("section, content")
      .eq("lang", lang);
    if (error) toast.error(error.message);
    const next = emptyOverrides();
    for (const row of data ?? []) {
      const section = row.section as HomepageSection;
      if (HOMEPAGE_SECTIONS.includes(section)) {
        next[section] = (row.content as Record<string, unknown>) ?? {};
      }
    }
    setOverrides(next);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  async function saveSection(section: HomepageSection) {
    setSaving(section);
    const { error } = await supabase
      .from("homepage_content")
      .upsert(
        { lang, section, content: overrides[section] as never },
        { onConflict: "lang,section" },
      );
    setSaving(null);
    if (error) return toast.error("Opslaan mislukt: " + error.message);
    toast.success("Opgeslagen");
    qc.invalidateQueries({ queryKey: ["homepage-content", lang] });
  }

  async function resetSection(section: HomepageSection) {
    if (!confirm(`Sectie "${SECTION_LABELS[section]}" terug naar standaard voor ${LANG_LABELS[lang]}?`)) return;
    const { error } = await supabase
      .from("homepage_content")
      .delete()
      .eq("lang", lang)
      .eq("section", section);
    if (error) return toast.error(error.message);
    toast.success("Teruggezet naar standaard");
    setOverrides((prev) => ({ ...prev, [section]: {} }));
    qc.invalidateQueries({ queryKey: ["homepage-content", lang] });
  }

  function updateField(section: HomepageSection, path: string, value: unknown) {
    setOverrides((prev) => ({
      ...prev,
      [section]: setPath(prev[section] ?? {}, path, value),
    }));
  }

  return (
    <div className="min-h-screen bg-background px-5 py-10 md:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono-cinema text-[10px] text-gold">— Admin</p>
            <h1 className="mt-2 font-display text-3xl font-medium md:text-4xl">Homepage-teksten</h1>
            <p className="mt-2 text-sm text-foreground/70">
              Overrides op de standaardteksten. Laat een veld leeg om de standaard terug te zetten.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={pathForLang(lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline"
            >
              Bekijk pagina <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Select value={lang} onValueChange={(v) => setLang(v as Lang)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => (
                  <SelectItem key={l} value={l}>
                    {LANG_LABELS[l]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <EarlyAccessToggle />

        {loading ? (
          <div className="py-16 text-center text-foreground/70">Laden…</div>
        ) : (
          <Tabs defaultValue="hero" className="w-full">
            <TabsList className="mb-4 flex flex-wrap">
              {HOMEPAGE_SECTIONS.map((s) => (
                <TabsTrigger key={s} value={s}>
                  {SECTION_LABELS[s]}
                </TabsTrigger>
              ))}
            </TabsList>

            {HOMEPAGE_SECTIONS.map((section) => (
              <TabsContent key={section} value={section}>
                <div className="rounded-2xl border border-border bg-night-deep/50 p-5 md:p-7">
                  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-display text-xl">{SECTION_LABELS[section]}</h2>
                    <div className="flex gap-2">
                      <Button
                        variant="ghostLine"
                        size="sm"
                        onClick={() => resetSection(section)}
                      >
                        <RotateCcw className="h-4 w-4" /> Alles resetten
                      </Button>
                      <Button
                        variant="hero"
                        size="sm"
                        onClick={() => saveSection(section)}
                        disabled={saving === section}
                      >
                        <Save className="h-4 w-4" /> {saving === section ? "Bezig…" : "Opslaan"}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {SECTION_SCHEMAS[section].map((field, idx) => (
                      <FieldRenderer
                        key={idx}
                        field={field}
                        pathPrefix=""
                        defaultsRoot={
                          (defaults as unknown as Record<string, unknown>)[section] as Record<string, unknown>
                        }
                        overrideRoot={overrides[section]}
                        onChange={(p, v) => updateField(section, p, v)}
                      />
                    ))}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
      <Toaster position="top-center" />
    </div>
  );
}

// ---------- Field rendering ----------

function joinPath(prefix: string, path: string) {
  return prefix ? `${prefix}.${path}` : path;
}

function FieldRenderer({
  field,
  pathPrefix,
  defaultsRoot,
  overrideRoot,
  onChange,
}: {
  field: FieldDef;
  pathPrefix: string;
  defaultsRoot: Record<string, unknown>;
  overrideRoot: Record<string, unknown>;
  onChange: (path: string, value: unknown) => void;
}) {
  if (field.kind === "list") {
    const listPath = joinPath(pathPrefix, field.path);
    const defaultList = (getPath(defaultsRoot, field.path) as unknown[]) ?? [];
    return (
      <div>
        <div className="mb-2 font-mono-cinema text-[10px] uppercase tracking-widest text-gold/80">
          {field.label}
        </div>
        <div className="space-y-3">
          {defaultList.map((defItem, i) => {
            const itemRecord = (defItem as Record<string, unknown>) ?? {};
            return (
              <details
                key={i}
                className="group rounded-lg border border-border bg-night/40"
                open={i === 0}
              >
                <summary className="cursor-pointer list-none px-4 py-3 text-sm text-foreground/80 hover:text-foreground">
                  <span className="mr-2 text-foreground/40">▸</span>
                  {field.itemLabel(i, itemRecord)}
                </summary>
                <div className="space-y-4 border-t border-border px-4 py-4">
                  {field.fields.map((sub, k) => (
                    <FieldRenderer
                      key={k}
                      field={sub}
                      pathPrefix={`${listPath}.${i}`}
                      defaultsRoot={defaultsRoot}
                      overrideRoot={overrideRoot}
                      onChange={onChange}
                    />
                  ))}
                </div>
              </details>
            );
          })}
        </div>
      </div>
    );
  }

  const fullPath = joinPath(pathPrefix, field.path);
  const defaultValue = getPath(defaultsRoot, fullPath);
  const overrideValue = getPath(overrideRoot, fullPath);
  // Arrays of strings (e.g. practical.cards[i].lines) render as textarea (one per line).
  const isStringArray = Array.isArray(defaultValue) && (defaultValue as unknown[]).every((v) => typeof v === "string");
  const defaultStr = isStringArray
    ? (defaultValue as string[]).join("\n")
    : typeof defaultValue === "string"
      ? defaultValue
      : "";
  const overrideStr =
    overrideValue === undefined || overrideValue === null
      ? ""
      : isStringArray && Array.isArray(overrideValue)
        ? (overrideValue as string[]).join("\n")
        : typeof overrideValue === "string"
          ? overrideValue
          : "";
  const displayValue = overrideStr || defaultStr;
  const isOverridden = overrideStr.length > 0;

  function handleChange(next: string) {
    if (next === defaultStr || next === "") {
      onChange(fullPath, isStringArray ? [] : "");
      return;
    }
    if (isStringArray) {
      onChange(fullPath, next.split("\n"));
    } else {
      onChange(fullPath, next);
    }
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <Label className="text-xs text-foreground/80">
          {field.label}
          {isOverridden && (
            <span className="ml-2 rounded-full bg-gold/15 px-1.5 py-0.5 font-mono-cinema text-[9px] uppercase tracking-widest text-gold">
              Aangepast
            </span>
          )}
        </Label>
        {isOverridden && (
          <button
            type="button"
            onClick={() => onChange(fullPath, isStringArray ? [] : "")}
            className="text-[11px] text-foreground/60 hover:text-gold"
          >
            Reset
          </button>
        )}
      </div>
      {field.kind === "rich" ? (
        <RichTextEditor value={displayValue} onChange={handleChange} minHeight={120} />
      ) : field.kind === "textarea" || isStringArray ? (
        <Textarea
          value={displayValue}
          onChange={(e) => handleChange(e.target.value)}
          rows={isStringArray ? Math.max(3, defaultStr.split("\n").length + 1) : 4}
          className="font-normal"
        />
      ) : (
        <Input value={displayValue} onChange={(e) => handleChange(e.target.value)} />
      )}
      {field.help ? <p className="mt-1 text-[11px] text-foreground/50">{field.help}</p> : null}
      {!isOverridden && (
        <p className="mt-1 text-[11px] text-foreground/40">Standaardtekst; typ om te overschrijven.</p>
      )}
    </div>
  );
}

function EarlyAccessToggle() {
  const [enabled, setEnabled] = useState<boolean>(true);

  async function load() {
    const { data } = await (supabase as any)
      .from("site_settings")
      .select("value")
      .eq("key", "early_access_form_visible")
      .maybeSingle();
    if (!data) return setEnabled(true);
    setEnabled(data.value === true || data.value === "true");
  }

  useEffect(() => {
    load();
  }, []);

  async function toggle(next: boolean) {
    setEnabled(next);
    const { error } = await (supabase as any)
      .from("site_settings")
      .upsert({ key: "early_access_form_visible", value: next });
    if (error) {
      toast.error(error.message);
      load();
      return;
    }
    toast.success(next ? "Early access-formulier staat aan" : "Early access-formulier staat uit");
  }

  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gold/30 bg-night-deep/40 p-5">
      <div>
        <p className="font-display text-lg">Early access aanmeldformulier</p>
        <p className="mt-1 text-sm text-foreground/70">
          Zet de hele aanmeldsectie op de homepage aan of uit.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-foreground/70">{enabled ? "Aan" : "Uit"}</span>
        <Switch checked={enabled} onCheckedChange={toggle} />
      </div>
    </div>
  );
}
